import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './RunsList.css';

const RunsList = () => {
  usePageTitle('Runs');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteModal, setDeleteModal] = useState({ show: false, run: null });
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const navigate = useNavigate();

  const statusFilter = searchParams.get('status') || 'all';
  const flowFilter = searchParams.get('flow') || '';

  const fetchRuns = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (flowFilter) {
        params.flow = flowFilter;
      }

      const response = await axiosInstance.get('/etl/runs/', { params });
      setRuns(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load runs');
      setLoading(false);
    }
  }, [statusFilter, flowFilter]);

  useEffect(() => {
    fetchRuns();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchRuns, 10000);
    return () => clearInterval(interval);
  }, [fetchRuns]);

  const setStatusFilter = (status) => {
    const params = new URLSearchParams(searchParams);
    if (status === 'all') {
      params.delete('status');
    } else {
      params.set('status', status);
    }
    setSearchParams(params);
  };

  const cancelRun = async (runId) => {
    if (!confirm('Are you sure you want to cancel this run?')) return;

    try {
      await axiosInstance.post(`/etl/runs/${runId}/cancel/`);
      alert('Run cancelled successfully!');
      fetchRuns();
    } catch (err) {
      alert('Failed to cancel run: ' + (err.response?.data?.error || err.message));
    }
  };

  const retryRun = async (run) => {
    if (!confirm(`Retry flow "${run.flow_name}"?`)) return;

    try {
      await axiosInstance.post(`/etl/flows/${run.flow}/trigger/`);
      alert('Flow triggered successfully!');
      fetchRuns();
    } catch (err) {
      alert('Failed to retry: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteRun = async () => {
    if (!deleteModal.run) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(`/etl/runs/${deleteModal.run.id}/`);
      setDeleteModal({ show: false, run: null });
      
      // Update runs list and adjust pagination
      const updatedRuns = runs.filter(r => r.id !== deleteModal.run.id);
      setRuns(updatedRuns);
      
      // Adjust page if needed after deletion
      const totalPages = Math.ceil(updatedRuns.length / itemsPerPage);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      }
    } catch (err) {
      alert('Failed to delete run: ' + (err.response?.data?.error || err.message));
      fetchRuns(); // Refresh on error
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'warning', icon: null },
      extracting: { class: 'info', icon: null },
      uploading: { class: 'info', icon: null },
      processing: { class: 'info', icon: null },
      loading: { class: 'info', icon: null },
      completed: { class: 'success', icon: null },
      failed: { class: 'error', icon: null },
      cancelled: { class: 'secondary', icon: null },
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isRunning = (status) => {
    return ['pending', 'extracting', 'uploading', 'processing', 'loading'].includes(status);
  };

  const handleSelectRun = (runId) => {
    setSelectedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRuns.size === paginatedRuns.length) {
      setSelectedRuns(new Set());
    } else {
      setSelectedRuns(new Set(paginatedRuns.map(run => run.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRuns.size === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedRuns.size} selected runs?`)) return;

    setDeleting(true);
    try {
      await axiosInstance.post('/etl/runs/bulk_delete/', {
        run_ids: Array.from(selectedRuns)
      });
      setSelectedRuns(new Set());
      fetchRuns();
    } catch (err) {
      alert('Failed to delete runs: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(runs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRuns = runs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, flowFilter]);

  if (loading) {
    return (
      <div className="runs-container">
        <div className="loading">Loading runs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="runs-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="runs-container">
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, run: null })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Run</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete run <strong>{deleteModal.run?.run_id}</strong>?</p>
              <p className="modal-warning">This action cannot be undone. All run data and logs will be permanently deleted.</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteModal({ show: false, run: null })}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={deleteRun}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Run'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1>ETL Runs</h1>
          <p className="page-description">Monitor and track all ETL job executions</p>
        </div>
        <div className="header-actions">
          {selectedRuns.size > 0 && (
            <button 
              onClick={handleBulkDelete} 
              className="btn-bulk-delete"
              disabled={deleting}
            >
              Delete Selected ({selectedRuns.size})
            </button>
          )}
          <button onClick={fetchRuns} className="btn-refresh">
            Refresh
          </button>
        </div>
      </div>

      {/* Status Filter */}
      <div className="filter-tabs">
        <button
          className={`tab ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All Runs
        </button>
        <button
          className={`tab ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('completed')}
        >
          Completed
        </button>
        <button
          className={`tab ${statusFilter === 'failed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('failed')}
        >
          Failed
        </button>
        <button
          className={`tab ${statusFilter === 'processing' ? 'active' : ''}`}
          onClick={() => setStatusFilter('processing')}
        >
          Running
        </button>
        <button
          className={`tab ${statusFilter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setStatusFilter('cancelled')}
        >
          Cancelled
        </button>
      </div>

      {/* Runs Table */}
      {runs.length === 0 ? (
        <div className="empty-state">
          <h3>No runs found</h3>
          <p>Trigger an ETL flow to see runs here</p>
          <button onClick={() => navigate('/flows')} className="btn-primary">
            Go to Flows
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="runs-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input 
                    type="checkbox" 
                    checked={paginatedRuns.length > 0 && selectedRuns.size === paginatedRuns.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Status</th>
                <th>Run ID</th>
                <th>Flow</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Records</th>
                <th>Triggered By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRuns.map((run) => {
                const statusConfig = getStatusBadge(run.status);
                const isSelected = selectedRuns.has(run.id);
                return (
                  <tr 
                    key={run.id} 
                    className={`${isRunning(run.status) ? 'row-running' : ''} ${isSelected ? 'row-selected' : ''}`}
                    onClick={() => handleSelectRun(run.id)}
                  >
                    <td className="checkbox-col" onClick={e => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => handleSelectRun(run.id)}
                      />
                    </td>
                    <td>
                      <span className={`status-badge badge-${statusConfig.class}`}>
                        {statusConfig.icon && <span className="status-icon">{statusConfig.icon}</span>}
                        <span>{run.status}</span>
                      </span>
                    </td>
                    <td>
                      <code className="run-id">{run.run_id}</code>
                    </td>
                    <td>
                      <span className="flow-name">{run.flow_name}</span>
                    </td>
                    <td>{formatDate(run.started_at)}</td>
                    <td>
                      {isRunning(run.status) ? (
                        <span className="duration-running">Running...</span>
                      ) : (
                        formatDuration(run.duration_seconds)
                      )}
                    </td>
                    <td>
                      <div className="records-info">
                        {run.records_extracted ? (
                          <>
                            <span className="records-loaded">{run.records_loaded || 0}</span>
                            <span className="records-separator">/</span>
                            <span className="records-total">{run.records_extracted}</span>
                            {run.records_failed > 0 && (
                              <span className="records-failed">
                                ({run.records_failed} failed)
                              </span>
                            )}
                          </>
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </td>
                    <td>{run.triggered_by_username || 'System'}</td>
                    <td>
                      <div className="action-buttons" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/runs/${run.id}`)}
                          className="btn-small btn-view"
                          title="View Details"
                        >
                          View
                        </button>
                        {isRunning(run.status) && (
                          <button
                            onClick={() => cancelRun(run.id)}
                            className="btn-small btn-cancel"
                            title="Cancel Run"
                          >
                            Cancel
                          </button>
                        )}
                        {(run.status === 'failed' || run.status === 'cancelled') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              retryRun(run);
                            }}
                            className="btn-small btn-retry"
                            title="Retry Flow"
                          >
                            Retry
                          </button>
                        )}
                        {!isRunning(run.status) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({ show: true, run });
                            }}
                            className="btn-small btn-delete"
                            title="Delete Run"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default RunsList;

